<template>
    <div class="show-purchase">
        <slot name="plans-header">
            <p class="title">
                Choose your payment plan
            </p>
        </slot>
        <billing-plans
            :billing-plan="billingPlan"
            :active-price="activePrice"
            @change="updatePrice"
        />
        <template v-if="isLoading">
            <base-loader
                class="show-info-loader"
            />
        </template>
        <template v-else-if="false === userCard">
            <slot name="credit-card-header" />
            <card-form
                :external-form-processing="formProcessing"
                :subscribe-card-error="cardError"
                :billing-plan="billingPlan"
                @stripe-success="subscribeUser"
            />
        </template>
        <template v-else>
            <edit-card
                :editing-card="editingCard"
                :without-subscription="true"
                @change="value => editingCard = value"
                @update-finished="loadCard"
            />
            <coupon-code
                v-if="! editingCard"
                :billing-plan="billingPlan"
                @change="changeCouponCode"
            />
            <base-submit-button
                v-if="! editingCard"
                class="btn update-subscription-button"
                :loading="formProcessing"
                :disabled="!activePrice"
                @click="subscribeUser(userCard.id)"
            >
                Confirm and subscribe
            </base-submit-button>
        </template>
    </div>
</template>

<script>
import { isEmpty } from 'lodash';
import { mapActions, mapState } from 'vuex';
import apiCall from '@/utils/apiCall';
import BillingPlans from '@/components/Billing/BillingPlans';
import CardForm from '@/components/Billing/CardForm';
import CouponCode from '@/components/Billing/CouponCode';
import EditCard from './EditCard';

const { lmFinished } = window;

export default {
    components: {
        BillingPlans,
        CardForm,
        EditCard,
        CouponCode,
    },

    props: {
        loading: {
            type: Boolean,
            required: true,
        },
        billingPlan: {
            required: true,
            type: [Object, Boolean],
        },
    },

    data: () => ({
        activePrice: null,
        formProcessing: false,
        cardError: null,
        defaultPaymentMethod: null,
        editingCard: false,
        couponCode: null,
    }),

    computed: {
        ...mapState(['userCard']),

        /**
         * Whether the component is in loading state.
         *
         * @returns  {boolean} Loading state indicator.
         */
        isLoading() {
            return this.loading || (false !== this.userCard && isEmpty(this.userCard));
        },
    },

    /**
     * Created hook
     *
     */
    mounted() {
        this.loadCard();
    },

    methods: {
        ...mapActions([
            'updateUser',
            'loadUserSubscription',
            'loadUserCard',
        ]),

        isEmpty,

        /**
         * Handle price change. Stores selected price object in local state.
         *
         * @param {object} price Stripe price object.
         */
        updatePrice(price) {
            this.activePrice = price;
        },

        /**
         * Creates user subscription
         *
         * @param {string} payment_method Payment method.
         * @param {string|null} couponCode     Coupon code.
         */
        async subscribeUser(payment_method, couponCode = null) {
            this.$emit('subscribing-user');

            const data = {
                price_ids: [this.activePrice.id],
                payment_method,
                lmref: lmFinished ? lmFinished() : null,
            };

            if (this.couponCode || couponCode) {
                data.promo = this.couponCode ?? couponCode;
            }

            const response = await apiCall(this, 'post', '/api/billing/subscriptions', data);

            if (201 === response.status) {
                await this.updateUser();
                await this.loadUserSubscription({ force: true });

                if (this.couponCode) {
                    this.$cookies.remove('fusebox_promo', '/', `.${window.location.host}`);
                }

                this.$emit('subscription-finished');
            } else {
                this.cardError = response.data.message;
            }
        },

        /**
         * Load user card
         *
         */
        async loadCard() {
            await this.loadUserCard({ withoutSubscription: true });
        },

        /**
         * * Sets new coupon code value.
         *
         * @param {string|null} couponCode Coupon code.
         */
        changeCouponCode(couponCode) {
            this.couponCode = couponCode;
        },
    },
};
</script>
